/**
 * Deal-stage DISPLAY helpers — human labels, emoji, and notification headers.
 *
 * The canonical enum (`DEAL_STAGE_VALUES` / `DealStage`) lives in
 * `deal-stages.ts`; this module is the ONE place that turns a stage VALUE into
 * something a human reads (Slack cards, notifications, UI badges). The maps are
 * typed `Record<DealStage, …>` on purpose: adding or renaming a stage in the
 * enum fails `tsc` here until it is given a label/emoji/header — the enum drives
 * display, never a stale hand-maintained copy. (This is the fix for the local
 * map that printed raw keys like `wtc_lost_no_budget` and gave every lost deal
 * the same neutral header.)
 */

import type { DealStage } from "@/lib/types/database/deals";
import { DEAL_STAGE_VALUES, isLostStage } from "@/lib/constants/deal-stages";

/** Shared header for every lost reason — reps only need "this deal is lost". */
const DEAL_LOST_HEADER = "💔 Deal Lost";

/** Neutral fallback emoji for an unrecognized / legacy stage string. */
const UNKNOWN_STAGE_EMOJI = "⭕";

/**
 * Human labels, EXHAUSTIVE over the enum. The `Record<DealStage, string>` type
 * is the point: a new/renamed stage breaks the build until it is labeled here.
 */
export const DEAL_STAGE_LABELS: Record<DealStage, string> = {
  lead: "Lead",
  qualified: "Qualified",
  discovery: "Discovery",
  proposal: "Proposal",
  negotiation: "Negotiation",
  contract_signed: "Contract Signed",
  deal_won: "Won",
  hand_off: "Hand-off",
  started: "Started",
  in_progress: "In Progress",
  completed: "Completed",
  invoiced: "Invoiced",
  paid: "Paid",
  sm_lost_no_savings: "Lost — No Savings",
  sm_lost_savings_customer_rejected: "Lost — Savings Rejected",
  wtc_lost_solution_rejected: "Lost — Solution Rejected",
  wtc_lost_over_budget: "Lost — Over Budget",
  wtc_lost_no_budget: "Lost — No Budget",
};

/** Per-stage emoji, EXHAUSTIVE over the enum (badge / inline use). */
export const DEAL_STAGE_EMOJI: Record<DealStage, string> = {
  lead: "🔵",
  qualified: "🟡",
  discovery: "🔎",
  proposal: "🟠",
  negotiation: "🟣",
  contract_signed: "📝",
  deal_won: "🟢",
  hand_off: "🟢",
  started: "🚧",
  in_progress: "🚧",
  completed: "🟢",
  invoiced: "🧾",
  paid: "🟢",
  sm_lost_no_savings: "⚫",
  sm_lost_savings_customer_rejected: "⚫",
  wtc_lost_solution_rejected: "⚫",
  wtc_lost_over_budget: "⚫",
  wtc_lost_no_budget: "⚫",
};

/**
 * Notification header (emoji + phrase), EXHAUSTIVE over the enum. All lost
 * reasons collapse to one "Deal Lost" header; the rest are stage-specific.
 */
const DEAL_STAGE_HEADERS: Record<DealStage, string> = {
  lead: "🆕 New Lead",
  qualified: "✅ Qualified",
  discovery: "🔍 Discovery",
  proposal: "📋 Proposal Stage",
  negotiation: "🤝 Negotiation Stage",
  contract_signed: "✍️ Contract Signed",
  deal_won: "🏆 Deal Won",
  hand_off: "🤝 Hand-off",
  started: "🚀 Work Started",
  in_progress: "🚀 Work Started",
  completed: "🎯 Deal Completed",
  invoiced: "🧾 Deal Invoiced",
  paid: "💰 Deal Paid",
  sm_lost_no_savings: DEAL_LOST_HEADER,
  sm_lost_savings_customer_rejected: DEAL_LOST_HEADER,
  wtc_lost_solution_rejected: DEAL_LOST_HEADER,
  wtc_lost_over_budget: DEAL_LOST_HEADER,
  wtc_lost_no_budget: DEAL_LOST_HEADER,
};

const KNOWN_DEAL_STAGES = new Set<string>(DEAL_STAGE_VALUES);

/** True when `stage` is a recognized `DealStage` (narrows the type). */
function isKnownDealStage(stage: string): stage is DealStage {
  return KNOWN_DEAL_STAGES.has(stage);
}

/**
 * Humanize an unknown/legacy stage string: `some_new_stage` → `Some New Stage`.
 * Only reached when a value is not (yet) in the enum — never prints the raw key.
 */
function humanizeStage(stage: string): string {
  return stage
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Human label for a stage. `null`/`undefined` → `"—"`; a recognized stage → its
 * canonical label; anything else → a humanized fallback (never the raw key).
 */
export function dealStageLabel(stage: string | null | undefined): string {
  if (!stage) return "—";
  if (isKnownDealStage(stage)) return DEAL_STAGE_LABELS[stage];
  return humanizeStage(stage);
}

/** Emoji for a stage; unknown/empty → a neutral fallback. */
export function dealStageEmoji(stage: string | null | undefined): string {
  if (!stage) return UNKNOWN_STAGE_EMOJI;
  if (isKnownDealStage(stage)) return DEAL_STAGE_EMOJI[stage];
  return UNKNOWN_STAGE_EMOJI;
}

/**
 * Notification header (emoji + phrase) for a destination stage. Every lost
 * reason renders "💔 Deal Lost" (guarded by `isLostStage`, so it stays uniform
 * even if the header map drifts); an unknown string gets a humanized header.
 */
export function dealStageHeader(stage: string): string {
  if (isKnownDealStage(stage)) {
    if (isLostStage(stage)) return DEAL_LOST_HEADER;
    return DEAL_STAGE_HEADERS[stage];
  }
  return `🟦 ${humanizeStage(stage)}`;
}
